import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ta from './locales/ta.json';
import hi from './locales/hi.json';
import te from './locales/te.json';
import ml from './locales/ml.json';
import kn from './locales/kn.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';
import ar from './locales/ar.json';
import {
  APP_LANGUAGE_CODES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  languageDir,
  resolveLanguage,
} from './languages';

function browserLanguage(): string {
  try {
    return resolveLanguage(navigator.language);
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

/** Initial language: saved choice wins, otherwise the browser locale. */
export function initialLanguage(): string {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved) return resolveLanguage(saved);
  } catch {
    /* storage unavailable (private mode / SSR) — fall through */
  }
  return browserLanguage();
}

function applyDocumentDir(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = languageDir(lng);
}

/**
 * Switch language everywhere: i18next, `document` (lang + RTL dir for
 * Arabic), and persisted choice. Returns the resolved language code.
 */
export async function setLanguage(lng: string): Promise<string> {
  const resolved = resolveLanguage(lng);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, resolved);
  } catch {
    /* persistence is best-effort */
  }
  applyDocumentDir(resolved);
  await i18n.changeLanguage(resolved);
  return resolved;
}

const initial = initialLanguage();
applyDocumentDir(initial);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ta: { translation: ta },
    hi: { translation: hi },
    te: { translation: te },
    ml: { translation: ml },
    kn: { translation: kn },
    es: { translation: es },
    fr: { translation: fr },
    pt: { translation: pt },
    ar: { translation: ar },
  },
  lng: initial,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: APP_LANGUAGE_CODES,
  nonExplicitSupportedLngs: true,
  load: 'languageOnly',
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lng) => applyDocumentDir(resolveLanguage(lng)));

export default i18n;

